import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Sparkles, Loader2, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/contexts/AuthContext';

const OBJECTIFS = [
  { value: 'decouverte', label: '🔍 Découverte (1er contact)' },
  { value: 'rdv', label: '📅 Obtenir un RDV' },
  { value: 'relance', label: '🔁 Relance après contact' },
  { value: 'objection', label: '🛡️ Répondre à une objection' },
];
const TONS = [
  { value: 'amical', label: '😊 Amical' },
  { value: 'direct', label: '🎯 Direct' },
  { value: 'expert', label: '🎓 Expert-conseil' },
];

// Minimal markdown renderer (H1, H2, H3, list, bold)
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listBuf = [];
  const flushList = (key) => {
    if (listBuf.length) {
      out.push(<ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 mb-2">{listBuf}</ul>);
      listBuf = [];
    }
  };
  lines.forEach((line, i) => {
    if (!line.trim()) { flushList(i); return; }
    if (line.startsWith('### ')) { flushList(i); out.push(<h4 key={i} className="text-sm font-semibold mt-3 mb-1">{line.slice(4)}</h4>); return; }
    if (line.startsWith('## ')) { flushList(i); out.push(<h3 key={i} className="text-base font-bold mt-3 mb-2 text-[#F97316]">{line.slice(3)}</h3>); return; }
    if (line.startsWith('# ')) { flushList(i); out.push(<h2 key={i} className="text-lg font-bold mt-2 mb-2">{line.slice(2)}</h2>); return; }
    if (line.startsWith('---')) { flushList(i); out.push(<hr key={i} className="my-2 border-[#E5E7EB]"/>); return; }
    if (/^\s*[-*]\s/.test(line) || /^\s*\d+\.\s/.test(line)) {
      const content = line.replace(/^\s*(?:[-*]|\d+\.)\s/, '');
      listBuf.push(<li key={`li-${i}`} dangerouslySetInnerHTML={{ __html: content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }}/>);
      return;
    }
    flushList(i);
    out.push(<p key={i} className="text-sm mb-2" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }}/>);
  });
  flushList('end');
  return out;
}

export default function CallScriptDialog({ prospectId, prospectName, trigger }) {
  const [open, setOpen] = useState(false);
  const [objectif, setObjectif] = useState('rdv');
  const [ton, setTon] = useState('amical');
  const [script, setScript] = useState('');
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true); setScript('');
    try {
      const { data } = await api.post('/ai/call-script', { prospect_id: prospectId, objectif, ton });
      setScript(data.script);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur génération");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(script);
    toast.success("Script copié");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div onClick={() => setOpen(true)}>{trigger}</div>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="call-script-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#F97316]"/>Script d'appel IA
          </DialogTitle>
          <DialogDescription className="text-xs">
            Script personnalisé généré par Claude Sonnet pour <strong>{prospectName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 mt-2">
          <div className="flex-1">
            <Label className="text-xs">Objectif de l'appel</Label>
            <Select value={objectif} onValueChange={setObjectif}>
              <SelectTrigger data-testid="script-objectif-select"><SelectValue/></SelectTrigger>
              <SelectContent>{OBJECTIFS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label className="text-xs">Ton</Label>
            <Select value={ton} onValueChange={setTon}>
              <SelectTrigger data-testid="script-ton-select"><SelectValue/></SelectTrigger>
              <SelectContent>{TONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="self-end">
            <Button onClick={generate} disabled={loading} className="bg-[#F97316] hover:bg-[#EA580C]" data-testid="generate-script-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Sparkles className="w-4 h-4 mr-1"/>}
              {script ? 'Regénérer' : 'Générer'}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto mt-4 p-4 bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg min-h-[280px]" data-testid="script-output">
          {loading && (
            <div className="flex flex-col items-center justify-center h-full text-[#71717A]">
              <Loader2 className="w-8 h-8 animate-spin mb-2 text-[#F97316]"/>
              <p className="text-xs">Claude rédige votre script…</p>
            </div>
          )}
          {!loading && !script && (
            <div className="flex flex-col items-center justify-center h-full text-center text-[#71717A]">
              <Sparkles className="w-8 h-8 mb-2 text-[#D4D4D8]"/>
              <p className="text-xs">Choisissez l'objectif et cliquez sur <strong>Générer</strong></p>
              <p className="text-[10px] mt-1">L'IA analyse l'historique du prospect pour un script sur mesure.</p>
            </div>
          )}
          {!loading && script && <div className="prose prose-sm max-w-none">{renderMarkdown(script)}</div>}
        </div>

        <DialogFooter className="gap-2">
          {script && (
            <>
              <Button variant="outline" size="sm" onClick={copy} data-testid="copy-script-btn"><Copy className="w-3 h-3 mr-1"/>Copier</Button>
              <Button variant="outline" size="sm" onClick={generate} disabled={loading} data-testid="regen-script-btn"><RefreshCw className="w-3 h-3 mr-1"/>Regénérer</Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="close-script-btn">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
